import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, ChevronDown, ChevronRight, CheckCircle, Download, Edit2, FolderPlus, Layers, Loader2, Plus, Trash2, Upload, XCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { clsx } from "clsx";
import { useAuthStore } from "@/store/auth";
import { Spinner } from "@/components/ui/Spinner";
import {
  useAccessMeta,
  useCreateGroup,
  useCreateInstruction,
  useDeleteAllInstructions,
  useDeleteGroup,
  useDeleteInstruction,
  useInstructionGroups,
  useInstructions,
  useUpdateGroup,
  useUpdateInstruction,
} from "../hooks/useInstructions";
import { instructionsApi } from "../api/instructions";
import { bgTasksApi } from "@/features/bg_tasks/api/bgTasks";
import { GroupFormModal } from "../components/GroupFormModal";
import { InstructionFormModal } from "../components/InstructionFormModal";
import { VersionHistoryPanel } from "../components/VersionHistoryPanel";
import type { Instruction, InstructionFormData, GroupFormData, InstructionGroup } from "../types";
import type { AsyncTaskRecord } from "@/features/bg_tasks/types";

function ZipImportPanel() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isActive = (t: AsyncTaskRecord) => t.status === "pending" || t.status === "running";
  const isFinal = (t: AsyncTaskRecord) => t.status === "success" || t.status === "failure" || t.status === "cancelled";

  const { data: task } = useQuery({
    queryKey: ["bg-task-import-zip", taskId],
    queryFn: () => bgTasksApi.retrieve(taskId!),
    enabled: !!taskId,
    refetchInterval: (query) => (query.state.data && isActive(query.state.data) ? 1500 : false),
  });

  const handleFile = async (file: File) => {
    setError(null);
    setTaskId(null);
    setUploadPct(0);
    try {
      const result = await instructionsApi.importZip(file, setUploadPct);
      setUploadPct(null);
      setTaskId(result.task_id);
    } catch (e: unknown) {
      setUploadPct(null);
      const msg = (e as { response?: { data?: { errors?: string } } })?.response?.data?.errors;
      setError(msg ?? "Błąd przesyłania pliku.");
    }
  };

  const statusIcon = task
    ? task.status === "success"
      ? <CheckCircle size={14} className="text-green-500" />
      : task.status === "failure"
      ? <XCircle size={14} className="text-red-500" />
      : task.status === "cancelled"
      ? <XCircle size={14} className="text-gray-400" />
      : <Loader2 size={14} className="animate-spin text-[var(--color-primary)]" />
    : null;

  const statusLabel = task
    ? task.status === "success"
      ? "Import zakończony"
      : task.status === "failure"
      ? "Import zakończony błędem"
      : task.status === "cancelled"
      ? "Import anulowany"
      : "Import w trakcie..."
    : null;

  return (
    <div className="bg-white dark:bg-gray-800 p-4 mb-4 border-l-4 border-[var(--color-primary)]">
      <div className="flex items-start gap-3">
        <Upload size={16} className="text-[var(--color-primary)] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Import zbiorczy z ZIP</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Plik ZIP wygenerowany przez <code>pack.py</code> (manifest.json + PDFy).
          </p>

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploadPct !== null || (!!task && isActive(task) && !isFinal(task))}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {uploadPct !== null ? (
                <><Loader2 size={12} className="animate-spin" /> Przesyłam {uploadPct}%</>
              ) : (
                <><Upload size={12} /> Wybierz plik ZIP</>
              )}
            </button>
            {taskId && (
              <Link
                to="/dev/bg-tasks"
                className="text-xs text-[var(--color-primary)] dark:text-[var(--color-primary)] hover:underline"
              >
                Zadania automatyczne →
              </Link>
            )}
          </div>

          {task && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
              {statusIcon}
              <span>{statusLabel}</span>
              {task.status === "success" && task.result && (() => {
                const r = task.result as { summary?: { created?: number; updated?: number; skipped?: number; errors?: number } };
                const s = r.summary;
                return s ? (
                  <span className="ml-1 text-gray-400">
                    ({s.created} nowe, {s.updated} zaktualizowane, {s.skipped} pominięte{s.errors ? `, ${s.errors} błędów` : ""})
                  </span>
                ) : null;
              })()}
            </div>
          )}

          {error && (
            <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept=".zip"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = "";
        }}
      />
    </div>
  );
}

const FLAG_LABEL_FALLBACK: Record<string, string> = {
  can_approve_orders: "Zamawiający",
  has_orderer_permission: "Zamawiający",
  can_view_tenders: "Przetargi",
  has_view_tenders_permission: "Przetargi",
  can_calculate_pricing: "Wycena – kalkulacja",
  has_calculate_pricing_permission: "Wycena – kalkulacja",
  can_approve_pricing: "Wycena – akceptacja",
  has_approve_pricing_permission: "Wycena – akceptacja",
  can_issue_offers: "Oferty",
  has_issue_offers_permission: "Oferty",
  can_view_pricing_catalog: "Katalog wycen",
  has_view_pricing_catalog_permission: "Katalog wycen",
  is_ceo: "Prezes",
  is_fleet_manager: "Flota",
};

function AccessBadges({ roles, flags }: { roles: string[]; flags: string[] }) {
  const { data: meta } = useAccessMeta();
  const roleLabel = (v: string) => meta?.roles.find((r) => r.value === v)?.label ?? v;
  const flagLabel = (v: string) =>
    meta?.flags.find((f) => f.value === v)?.label ?? FLAG_LABEL_FALLBACK[v] ?? v;

  if (roles.length === 0 && flags.length === 0)
    return <span className="text-xs text-gray-400 italic">tylko dyrektor / CEO / dev</span>;

  // Flag-gated: role badges are misleading — show Director (implicit) + flag labels
  if (flags.length > 0) {
    return (
      <div className="flex flex-wrap gap-1">
        <span className="px-1.5 py-0.5 text-xs rounded bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] text-[var(--color-primary)] dark:text-[var(--color-primary)]">
          Dyrektor
        </span>
        {flags.map((v) => (
          <span key={v} className="px-1.5 py-0.5 text-xs rounded bg-indigo-50 dark:bg-indigo-900/30 text-[var(--color-primary)] dark:text-indigo-300">
            {flagLabel(v)}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      {roles.map((v) => (
        <span key={v} className="px-1.5 py-0.5 text-xs rounded bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] text-[var(--color-primary)] dark:text-[var(--color-primary)]">
          {roleLabel(v)}
        </span>
      ))}
    </div>
  );
}

function InstructionRow({
  instruction,
  isDev,
  canSeeVersions,
  onEdit,
  onDelete,
}: {
  instruction: Instruction;
  isDev: boolean;
  canSeeVersions: boolean;
  onEdit: (i: Instruction) => void;
  onDelete: (i: Instruction) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const latest = instruction.latest_version;

  return (
    <div className={clsx(
      "rounded-lg overflow-hidden",
      !instruction.is_active && "opacity-60"
    )}>
      <div
        className="flex items-center gap-2 sm:gap-3 px-2 sm:px-4 py-3 bg-white dark:bg-gray-900 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
        onClick={() => setExpanded((e) => !e)}
      >
        <span className="text-gray-400">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>

        <BookOpen size={15} className="text-[var(--color-primary)] flex-shrink-0" />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {instruction.title}
            </span>
            {!instruction.is_active && (
              <span className="px-1.5 py-0.5 text-xs rounded bg-gray-200 dark:bg-gray-700 text-gray-500">
                nieaktywna
              </span>
            )}
            {latest && (
              <span className="text-xs text-gray-400">v{latest.version_number}</span>
            )}
          </div>
          {instruction.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {instruction.description}
            </p>
          )}
        </div>

        {isDev && (
          <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => onEdit(instruction)}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
              title="Edytuj"
            >
              <Edit2 size={14} />
            </button>
            <button
              onClick={() => onDelete(instruction)}
              className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-gray-500 hover:text-red-500"
              title="Usuń"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}

        {latest?.file_url && (
          <a
            href={latest.file_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="p-1.5 rounded hover:bg-[var(--color-primary-dim)] dark:hover:bg-[var(--color-primary-dim)] text-[var(--color-primary)] flex-shrink-0"
            title="Pobierz aktualną wersję"
          >
            <Download size={14} />
          </a>
        )}
      </div>

      {expanded && (
        <div className="px-2 sm:px-4 pb-4 pt-2 bg-gray-50 dark:bg-gray-800/30 border-t border-gray-100 dark:border-gray-700 space-y-4">
          {canSeeVersions && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Dostęp</p>
              <AccessBadges roles={instruction.allowed_roles} flags={instruction.allowed_flags} />
            </div>
          )}
          {canSeeVersions && (
            <VersionHistoryPanel instructionId={instruction.id} canUpload={isDev} />
          )}
          {!canSeeVersions && latest?.file_url && (
            <a
              href={latest.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-[var(--color-primary)] dark:text-[var(--color-primary)] hover:underline"
            >
              <Download size={14} />
              Otwórz PDF ({latest.original_name})
            </a>
          )}
          {!latest && <p className="text-sm text-gray-400 italic">Brak pliku PDF.</p>}
        </div>
      )}
    </div>
  );
}

function GroupSection({
  group,
  isDev,
  canSeeVersions,
  onEditGroup,
  onDeleteGroup,
  onAddInstruction,
  onEditInstruction,
  onDeleteInstruction,
}: {
  group: InstructionGroup;
  isDev: boolean;
  canSeeVersions: boolean;
  onEditGroup: (g: InstructionGroup) => void;
  onDeleteGroup: (g: InstructionGroup) => void;
  onAddInstruction: (groupId: string) => void;
  onEditInstruction: (i: Instruction) => void;
  onDeleteInstruction: (i: Instruction) => void;
}) {
  const [collapsed, setCollapsed] = useState(true);
  const { data: instructions = [], isLoading } = useInstructions(group.id, !collapsed);

  return (
    <section>
      <div
        className="flex items-center justify-between px-2 sm:px-4 py-3 cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
        onClick={() => setCollapsed((c) => !c)}
      >
        <div className="flex items-center gap-2">
          <Layers size={15} className="text-gray-400" />
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{group.name}</span>
          <span className="text-xs text-gray-400">({group.instruction_count})</span>
          {collapsed ? <ChevronRight size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </div>
        {isDev && (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => onAddInstruction(group.id)}
              className="flex items-center gap-1 px-2 py-1 text-xs text-[var(--color-primary)] dark:text-[var(--color-primary)] hover:bg-[var(--color-primary-dim)] dark:hover:bg-[var(--color-primary-dim)] rounded"
            >
              <Plus size={12} /> <span className="hidden sm:inline">Instrukcja</span>
            </button>
            <button
              onClick={() => onEditGroup(group)}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
            >
              <Edit2 size={13} />
            </button>
            <button
              onClick={() => onDeleteGroup(group)}
              className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500"
            >
              <Trash2 size={13} />
            </button>
          </div>
        )}
      </div>

      {!collapsed && (
        <div className="space-y-2 pl-0 sm:pl-4">
          {isLoading && <Spinner />}
          {!isLoading && instructions.length === 0 && (
            <p className="text-sm text-gray-400 italic py-2">Brak instrukcji w tej grupie.</p>
          )}
          {[...instructions].sort((a, b) => a.title.localeCompare(b.title, "pl", { numeric: true })).map((instr) => (
            <InstructionRow
              key={instr.id}
              instruction={instr}
              isDev={isDev}
              canSeeVersions={canSeeVersions}
              onEdit={onEditInstruction}
              onDelete={onDeleteInstruction}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export function InstructionsPage() {
  const user = useAuthStore((s) => s.user);
  const isDev = !!user?.is_developer;
  const canSeeVersions = isDev || !!user?.is_ceo || user?.role === "director";

  const { data: groups = [], isLoading } = useInstructionGroups();

  const createGroup = useCreateGroup();
  const updateGroup = useUpdateGroup();
  const deleteGroup = useDeleteGroup();
  const createInstruction = useCreateInstruction();
  const updateInstruction = useUpdateInstruction();
  const deleteInstruction = useDeleteInstruction();
  const deleteAllInstructions = useDeleteAllInstructions();

  const canDeleteAll = isDev || user?.role === "director";

  const handleDeleteAll = async () => {
    if (!window.confirm("Usunąć WSZYSTKIE instrukcje? Tej operacji nie można cofnąć.")) return;
    await deleteAllInstructions.mutateAsync();
  };

  const [groupModal, setGroupModal] = useState<{ open: boolean; group?: InstructionGroup | null }>({ open: false });
  const [instrModal, setInstrModal] = useState<{ open: boolean; instruction?: Instruction | null; defaultGroup?: string }>({ open: false });

  const handleSaveGroup = async (data: GroupFormData) => {
    if (groupModal.group) {
      await updateGroup.mutateAsync({ id: groupModal.group.id, data });
    } else {
      await createGroup.mutateAsync(data);
    }
  };

  const handleSaveInstruction = async (data: InstructionFormData) => {
    if (instrModal.instruction) {
      await updateInstruction.mutateAsync({ id: instrModal.instruction.id, data });
    } else {
      await createInstruction.mutateAsync(data);
    }
  };

  const handleDeleteGroup = async (group: InstructionGroup) => {
    if (!window.confirm(`Usunąć grupę "${group.name}"? Możliwe tylko gdy jest pusta.`)) return;
    await deleteGroup.mutateAsync(group.id);
  };

  const handleDeleteInstruction = async (instr: Instruction) => {
    if (!window.confirm(`Usunąć instrukcję "${instr.title}"?`)) return;
    await deleteInstruction.mutateAsync(instr.id);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Instrukcje</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-1" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Stanowiskowe instrukcje obsługi systemu ELGW5
            {!isLoading && groups.length > 0 && (
              <span className="ml-2 text-xs text-[var(--color-primary)]">({groups.length} grup)</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:flex-shrink-0">
          {canDeleteAll && (
            <button
              onClick={handleDeleteAll}
              disabled={deleteAllInstructions.isPending}
              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Trash2 size={15} />
              {deleteAllInstructions.isPending ? "Usuwanie..." : "Usuń wszystkie"}
            </button>
          )}
          {isDev && (
            <button
              onClick={() => setGroupModal({ open: true, group: null })}
              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg hover:bg-gray-700 dark:hover:bg-gray-200 transition-colors"
            >
              <FolderPlus size={15} />
              Nowa grupa
            </button>
          )}
        </div>
      </div>

      {isDev && <ZipImportPanel />}

      {isLoading && (
        <div className="flex justify-center py-12"><Spinner /></div>
      )}

      {!isLoading && groups.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <BookOpen size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">Brak instrukcji.</p>
          {isDev && <p className="text-xs mt-1">Dodaj grupę, żeby zacząć.</p>}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
        {[...groups].sort((a, b) => a.name.localeCompare(b.name, "pl")).map((group) => (
          <GroupSection
            key={group.id}
            group={group}
            isDev={isDev}
            canSeeVersions={canSeeVersions}
            onEditGroup={(g) => setGroupModal({ open: true, group: g })}
            onDeleteGroup={handleDeleteGroup}
            onAddInstruction={(gId) => setInstrModal({ open: true, instruction: null, defaultGroup: gId })}
            onEditInstruction={(i) => setInstrModal({ open: true, instruction: i })}
            onDeleteInstruction={handleDeleteInstruction}
          />
        ))}
      </div>

      {groupModal.open && (
        <GroupFormModal
          group={groupModal.group}
          onSave={handleSaveGroup}
          onClose={() => setGroupModal({ open: false })}
        />
      )}

      {instrModal.open && (
        <InstructionFormModal
          instruction={instrModal.instruction}
          onSave={handleSaveInstruction}
          onClose={() => setInstrModal({ open: false })}
        />
      )}
    </div>
  );
}
